import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Rocket,
  CheckSquare,
  Users,
  LayoutList,
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Circle,
  AlertTriangle,
  Trash2,
  Clock,
  Sparkles,
  Loader2,
  Wand2,
  Save,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { apiClient } from "../services/api";

const TABS = [
  { id: "dashboard", icon: Rocket },
  { id: "onboardings", icon: Users },
  { id: "templates", icon: LayoutList },
  { id: "new", icon: Plus },
  { id: "ai", icon: Sparkles },
];

const CATEGORY_COLORS = {
  admin: "bg-blue-100 text-blue-700",
  it: "bg-purple-100 text-purple-700",
  training: "bg-yellow-100 text-yellow-700",
  social: "bg-green-100 text-green-700",
  role: "bg-orange-100 text-orange-700",
};
const CATEGORY_KEYS = ["admin", "it", "training", "social", "role"];
const catColor = (c) => CATEGORY_COLORS[c] || CATEGORY_COLORS.admin;

const STATUS_VARIANTS = { pending: "outline", in_progress: "default", completed: "secondary", cancelled: "destructive" };
const statusBadge = (s, t) => (
  <Badge variant={STATUS_VARIANTS[s] || "outline"}>{t(`onboardingHub.status.${s}`, { defaultValue: s })}</Badge>
);

const ProgressBar = ({ pct }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-green-500" : pct > 50 ? "bg-blue-500" : "bg-orange-400"}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
    <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
  </div>
);

// ---------------------------------------------------------------------------
// Tab: Dashboard
// ---------------------------------------------------------------------------
function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/onboarding/dashboard/summary");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
      <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      {t("onboardingHub.loading")}
    </div>
  );

  const kpis = [
    { label: t("onboardingHub.dashboard.kpiTotal"), value: data.total, icon: Users },
    { label: t("onboardingHub.dashboard.kpiInProgress"), value: data.in_progress, icon: Clock },
    { label: t("onboardingHub.dashboard.kpiCompleted"), value: data.completed, icon: CheckCircle },
    { label: t("onboardingHub.dashboard.kpiOverdue"), value: data.overdue_tasks, icon: AlertTriangle, warn: data.overdue_tasks > 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-1">
              <k.icon className={`h-4 w-4 ${k.warn ? "text-orange-500" : "text-muted-foreground"}`} />
              <p className="text-xs text-muted-foreground">{k.label}</p>
            </div>
            <p className={`text-2xl font-bold ${k.warn && k.value > 0 ? "text-orange-500" : ""}`}>
              {k.value}
            </p>
          </div>
        ))}
      </div>

      {data.avg_progress_pct > 0 && (
        <div className="p-4 border rounded-lg bg-card">
          <p className="text-sm font-medium mb-2">{t("onboardingHub.dashboard.avgProgress")}</p>
          <ProgressBar pct={data.avg_progress_pct} />
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
          {t("onboardingHub.dashboard.recentTitle")}
        </h3>
        {data.recent?.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("onboardingHub.dashboard.noActive")}</p>
        ) : (
          <div className="space-y-2">
            {data.recent?.map((o) => (
              <div key={o.id} className="p-3 border rounded-lg bg-card">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium">{o.employee_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {o.template_name || t("onboardingHub.noTemplate")} · {t("onboardingHub.dashboard.startedLabel", { date: o.start_date || "—" })}
                    </p>
                  </div>
                  {statusBadge(o.status, t)}
                </div>
                <ProgressBar pct={o.progress_pct} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Onboardings list
// ---------------------------------------------------------------------------
function OnboardingsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await apiClient.get(`v1/onboarding/${q}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    if (detail[id]) return;
    try {
      const r = await apiClient.get(`v1/onboarding/${id}`);
      setDetail((prev) => ({ ...prev, [id]: r.data }));
    } catch {
      setDetail((prev) => ({ ...prev, [id]: { error: true } }));
    }
  };

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    await loadDetail(id);
  };

  const completeTask = async (onboardingId, taskId) => {
    await apiClient.post(`v1/onboarding/${onboardingId}/tasks/${taskId}/complete`, {});
    const r = await apiClient.get(`v1/onboarding/${onboardingId}`);
    setDetail((prev) => ({ ...prev, [onboardingId]: r.data }));
    load();
  };

  const uncompleteTask = async (onboardingId, taskId) => {
    await apiClient.post(`v1/onboarding/${onboardingId}/tasks/${taskId}/uncomplete`);
    const r = await apiClient.get(`v1/onboarding/${onboardingId}`);
    setDetail((prev) => ({ ...prev, [onboardingId]: r.data }));
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          className="border rounded-md px-3 py-2 text-sm bg-background"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t("onboardingHub.onboardings.allStatuses")}</option>
          <option value="pending">{t("onboardingHub.onboardings.filterPending")}</option>
          <option value="in_progress">{t("onboardingHub.onboardings.filterInProgress")}</option>
          <option value="completed">{t("onboardingHub.onboardings.filterCompleted")}</option>
          <option value="cancelled">{t("onboardingHub.onboardings.filterCancelled")}</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <span className="text-sm text-muted-foreground ml-auto">{t("onboardingHub.onboardings.count", { count: total })}</span>
      </div>

      {items.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">{t("onboardingHub.onboardings.empty")}</p>
      ) : (
        <div className="space-y-2">
          {items.map((o) => {
            const d = detail[o.id];
            return (
              <div key={o.id} className="border rounded-lg bg-card overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30"
                  onClick={() => toggle(o.id)}
                >
                  <div className="flex items-center gap-3">
                    {expanded === o.id ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{o.employee_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {o.template_name || t("onboardingHub.noTemplate")} · {t("onboardingHub.onboardings.tasksCount", { completed: o.completed_tasks, total: o.total_tasks })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 hidden sm:block">
                      <ProgressBar pct={o.progress_pct} />
                    </div>
                    {statusBadge(o.status, t)}
                  </div>
                </div>

                {expanded === o.id && d && !d.error && (
                  <div className="border-t px-4 pb-4 pt-3 bg-muted/10 space-y-2">
                    {(d.tasks || []).map((task) => {
                      const isOverdue = task.due_date && !task.is_completed && new Date(task.due_date) < new Date();
                      return (
                        <div
                          key={task.id}
                          className={`flex items-start gap-3 p-2 rounded-lg ${task.is_completed ? "opacity-60" : ""} ${isOverdue ? "bg-red-50 dark:bg-red-950/20" : ""}`}
                        >
                          <button
                            onClick={() =>
                              task.is_completed
                                ? uncompleteTask(o.id, task.id)
                                : completeTask(o.id, task.id)
                            }
                            className="mt-0.5 flex-shrink-0"
                          >
                            {task.is_completed ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <Circle className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${task.is_completed ? "line-through" : ""}`}>
                              {task.title}
                              {task.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            {task.description && (
                              <p className="text-xs text-muted-foreground">{task.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-xs px-1.5 py-0.5 rounded ${catColor(task.category)}`}>{t(`onboardingHub.categories.${task.category}`, { defaultValue: task.category })}</span>
                              {task.due_date && (
                                <span className={`text-xs ${isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                                  {isOverdue ? "⚠ " : ""}{t("onboardingHub.onboardings.dueLabel", { date: new Date(task.due_date).toLocaleDateString() })}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground capitalize">{task.assignee_role}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Templates
// ---------------------------------------------------------------------------
function TemplatesTab() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", is_default: false });
  const [taskDefs, setTaskDefs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/onboarding/templates");
      setTemplates(r.data || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTaskDef = () => {
    setTaskDefs((prev) => [...prev, {
      title: "", category: "admin", due_day_offset: 1, assignee_role: "hr", required: true
    }]);
  };

  const updateTaskDef = (idx, field, val) => {
    setTaskDefs((prev) => prev.map((t, i) => i === idx ? { ...t, [field]: val } : t));
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      await apiClient.post("v1/onboarding/templates", {
        ...form,
        task_definitions: taskDefs,
      });
      setCreating(false);
      setForm({ name: "", description: "", is_default: false });
      setTaskDefs([]);
      load();
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm(t("onboardingHub.templates.confirmDelete"))) return;
    await apiClient.delete(`v1/onboarding/templates/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating((c) => !c)} variant={creating ? "outline" : "default"} size="sm">
          {creating ? t("common.cancel") : <><Plus className="h-4 w-4 mr-1" /> {t("onboardingHub.templates.new")}</>}
        </Button>
      </div>

      {creating && (
        <div className="border rounded-lg p-4 space-y-4 bg-card">
          <h3 className="font-semibold text-sm">{t("onboardingHub.templates.createTitle")}</h3>
          <div className="space-y-3">
            <input
              className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              placeholder={t("onboardingHub.templates.namePlaceholder")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <textarea
              className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none"
              rows={2}
              placeholder={t("onboardingHub.templates.descriptionPlaceholder")}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              />
              {t("onboardingHub.templates.isDefault")}
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{t("onboardingHub.templates.tasksCount", { count: taskDefs.length })}</span>
              <Button variant="outline" size="sm" onClick={addTaskDef}>
                <Plus className="h-4 w-4 mr-1" /> {t("onboardingHub.templates.addTask")}
              </Button>
            </div>
            <div className="space-y-2">
              {taskDefs.map((td, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    className="flex-1 border rounded px-2 py-1 text-sm bg-background"
                    placeholder={t("onboardingHub.templates.taskTitlePlaceholder")}
                    value={td.title}
                    onChange={(e) => updateTaskDef(idx, "title", e.target.value)}
                  />
                  <select
                    className="border rounded px-2 py-1 text-sm bg-background"
                    value={td.category}
                    onChange={(e) => updateTaskDef(idx, "category", e.target.value)}
                  >
                    {CATEGORY_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`onboardingHub.categories.${k}`)}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    className="w-16 border rounded px-2 py-1 text-sm bg-background"
                    title={t("onboardingHub.templates.dayOffsetTitle")}
                    value={td.due_day_offset}
                    min={0}
                    onChange={(e) => updateTaskDef(idx, "due_day_offset", Number(e.target.value))}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setTaskDefs((prev) => prev.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Button onClick={save} disabled={loading || !form.name.trim()} size="sm">
            <CheckCircle className="h-4 w-4 mr-1" /> {t("onboardingHub.templates.save")}
          </Button>
        </div>
      )}

      {templates.length === 0 && !creating ? (
        <p className="text-center text-sm text-muted-foreground py-8">{t("onboardingHub.templates.empty")}</p>
      ) : (
        <div className="space-y-2">
          {templates.map((tpl) => (
            <div key={tpl.id} className="flex items-center justify-between p-4 border rounded-lg bg-card">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{tpl.name}</p>
                  {tpl.is_default && <Badge variant="secondary">{t("onboardingHub.templates.defaultBadge")}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("onboardingHub.templates.taskCountLabel", { count: tpl.task_count })}
                  {tpl.description && ` · ${tpl.description}`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => deleteTemplate(tpl.id)}>
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Launch onboarding
// ---------------------------------------------------------------------------
function NewOnboardingTab({ onCreated }) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ employee_id: "", template_id: "", start_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data || []));
    apiClient.get("v1/onboarding/templates").then((r) => setTemplates(r.data || []));
  }, []);

  const submit = async () => {
    if (!form.employee_id) return;
    setLoading(true);
    try {
      await apiClient.post("v1/onboarding/", {
        employee_id: Number(form.employee_id),
        template_id: form.template_id ? Number(form.template_id) : null,
        start_date: form.start_date || null,
        notes: form.notes || null,
      });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onCreated?.(); }, 1500);
      setForm({ employee_id: "", template_id: "", start_date: "", notes: "" });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 max-w-md">
      {success && (
        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
          <CheckCircle className="h-4 w-4" /> {t("onboardingHub.newTab.success")}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">{t("onboardingHub.newTab.employee")}</label>
          <select
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
          >
            <option value="">{t("onboardingHub.newTab.selectEmployee")}</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name} — {e.registration_number}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">{t("onboardingHub.newTab.template")}</label>
          <select
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.template_id}
            onChange={(e) => setForm({ ...form, template_id: e.target.value })}
          >
            <option value="">{t("onboardingHub.newTab.autoDetect")}</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}{tpl.is_default ? t("onboardingHub.newTab.defaultSuffix") : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">{t("onboardingHub.newTab.startDate")}</label>
          <input
            type="date"
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          />
        </div>
        <div>
          <label className="text-sm font-medium">{t("onboardingHub.newTab.notes")}</label>
          <textarea
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background resize-none"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </div>

      <Button onClick={submit} disabled={loading || !form.employee_id}>
        {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
        {t("onboardingHub.newTab.launch")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Plan Generator Tab
// ---------------------------------------------------------------------------
function AIGeneratorTab({ onSavedTemplate }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState({ durations: [], categories: [] });
  const [roleTitle, setRoleTitle] = useState("");
  const [seniority, setSeniority] = useState("");
  const [country, setCountry] = useState("");
  const [contractType, setContractType] = useState("");
  const [durationDays, setDurationDays] = useState(90);
  const [customInstructions, setCustomInstructions] = useState("");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSave, setShowSave] = useState(false);
  const [tplName, setTplName] = useState("");

  useEffect(() => {
    apiClient.get("v1/onboarding/ai/options").then((r) => setOptions(r.data || { durations: [], categories: [] })).catch(() => {});
  }, []);

  const generate = async () => {
    if (!roleTitle.trim()) { setError(t("onboardingHub.ai.roleRequired")); return; }
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const r = await apiClient.post("v1/onboarding/ai/generate-plan", {
        role_title: roleTitle,
        seniority: seniority || null,
        country: country || null,
        contract_type: contractType || null,
        duration_days: parseInt(durationDays),
        custom_instructions: customInstructions || null,
      });
      setResult(r.data);
      setTplName(t("onboardingHub.ai.defaultTplName", { role: r.data.role_title }));
    } catch (e) {
      setError(e.response?.data?.detail || t("onboardingHub.ai.genError"));
    } finally {
      setGenerating(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return;
    setSaving(true);
    try {
      await apiClient.post("v1/onboarding/ai/save-as-template", {
        name: tplName,
        description: result?.summary,
        is_default: false,
        role_title: roleTitle,
        seniority: seniority || null,
        country: country || null,
        contract_type: contractType || null,
        duration_days: parseInt(durationDays),
        custom_instructions: customInstructions || null,
      });
      setShowSave(false);
      onSavedTemplate && onSavedTemplate();
    } catch (e) {
      setError(e.response?.data?.detail || t("onboardingHub.ai.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="p-3 rounded-lg text-xs flex items-center gap-2"
        style={{ background: "rgba(94,106,210,0.06)", border: "1px solid rgba(94,106,210,0.25)" }}>
        <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: "#5e6ad2" }} />
        <span>{t("onboardingHub.ai.banner")}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">{t("onboardingHub.ai.roleTitle")}</label>
          <input value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)}
            placeholder={t("onboardingHub.ai.roleTitlePlaceholder")}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">{t("onboardingHub.ai.seniority")}</label>
          <input value={seniority} onChange={(e) => setSeniority(e.target.value)}
            placeholder={t("onboardingHub.ai.seniorityPlaceholder")}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">{t("onboardingHub.ai.country")}</label>
          <input value={country} onChange={(e) => setCountry(e.target.value)}
            placeholder={t("onboardingHub.ai.countryPlaceholder")}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">{t("onboardingHub.ai.contractType")}</label>
          <input value={contractType} onChange={(e) => setContractType(e.target.value)}
            placeholder={t("onboardingHub.ai.contractTypePlaceholder")}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">{t("onboardingHub.ai.duration")}</label>
          <select value={durationDays} onChange={(e) => setDurationDays(parseInt(e.target.value))}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background">
            {(options.durations || []).map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>
        <div className="md:row-span-2">
          <label className="block text-xs font-medium mb-1 text-muted-foreground">{t("onboardingHub.ai.customInstructions")}</label>
          <textarea rows={4} value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder={t("onboardingHub.ai.customPlaceholder")}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={generate} disabled={generating || !roleTitle.trim()}>
          {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("onboardingHub.ai.generating")}</> : <><Wand2 className="h-4 w-4 mr-2" /> {t("onboardingHub.ai.generate")}</>}
        </Button>
        {result && (
          <Button variant="outline" onClick={() => setShowSave(true)}>
            <Save className="h-4 w-4 mr-2" /> {t("onboardingHub.ai.saveAsTemplate")}
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg border text-sm flex items-start gap-2 bg-red-50 border-red-200 text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {showSave && result && (
        <div className="p-4 rounded-lg border bg-card space-y-3">
          <p className="text-sm font-medium">{t("onboardingHub.ai.saveAsTemplate")}</p>
          <input value={tplName} onChange={(e) => setTplName(e.target.value)}
            placeholder={t("onboardingHub.ai.tplNamePlaceholder")}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background" />
          <div className="flex gap-2">
            <Button onClick={saveAsTemplate} disabled={saving || !tplName.trim()} size="sm">
              {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> {t("onboardingHub.ai.saving")}</> : t("onboardingHub.ai.save")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSave(false)}>{t("common.cancel")}</Button>
          </div>
          <p className="text-xs text-muted-foreground italic">
            {t("onboardingHub.ai.saveNote")}
          </p>
        </div>
      )}

      {result && (
        <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
          <div className="pb-3 border-b border-border">
            <p className="text-base font-semibold">{result.role_title} <span className="text-xs font-normal text-muted-foreground">{t("onboardingHub.ai.planMeta", { days: result.duration_days, count: result.task_count })}</span></p>
            {result.summary && <p className="text-sm italic text-muted-foreground mt-1">{result.summary}</p>}
          </div>

          {result.milestones?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">{t("onboardingHub.ai.milestones")}</p>
              <div className="flex flex-wrap gap-2">
                {result.milestones.map((m, i) => (
                  <Badge key={i} variant="outline">{t("onboardingHub.ai.milestoneBadge", { day: m.day_offset, label: m.label })}</Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t("onboardingHub.ai.detailedPlan")}</p>
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
              {result.tasks.map((task, i) => (
                <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-border bg-background">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap mt-0.5">J+{task.due_day_offset}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{task.title}</p>
                    {task.description && <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>}
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${catColor(task.category)}`}>{t(`onboardingHub.categories.${task.category}`, { defaultValue: task.category })}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">→ {task.assignee_role}</span>
                      {task.required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{t("onboardingHub.ai.required")}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {result.success_criteria?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">{t("onboardingHub.ai.successCriteria")}</p>
              <ul className="text-xs list-disc list-inside space-y-0.5">
                {result.success_criteria.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}

          <p className="text-[11px] italic text-muted-foreground pt-2 border-t border-border">{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Hub
// ---------------------------------------------------------------------------
export default function OnboardingHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const [refreshKey, setRefreshKey] = useState(0);

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":   return <DashboardTab key={refreshKey} />;
      case "onboardings": return <OnboardingsTab key={refreshKey} />;
      case "templates":   return <TemplatesTab />;
      case "new":         return <NewOnboardingTab onCreated={() => { setRefreshKey((k) => k + 1); setTab("onboardings"); }} />;
      case "ai":          return <AIGeneratorTab onSavedTemplate={() => setTab("templates")} />;
      default:            return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Rocket className="h-6 w-6 text-primary" />
          {t("onboardingHub.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("onboardingHub.subtitle")}
        </p>
      </div>

      <div className="border-b flex gap-1">
        {TABS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(`onboardingHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
